import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HttpError,
  IResourceComponentsProps,
  useApiUrl,
  useCustomMutation,
} from "@refinedev/core";
import { Edit, useForm } from "@refinedev/mantine";
import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Button,
  ColorInput,
  Grid,
  Group,
  ScrollArea,
  Select,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconFileText,
  IconForms,
  IconMail,
  IconMessage,
  IconPalette,
  IconDeviceFloppy,
  IconX,
  IconEye,
} from "@tabler/icons";
import {
  ReviewEmailTemplate,
  UpdateReviewEmailTemplate,
  FIELD_LABELS,
  FIELD_GROUPS,
  PLACEHOLDER_FIELDS,
  PLACEHOLDERS,
  FONT_OPTIONS,
  BRAND_LABELS,
  EMAIL_TYPE_LABELS,
} from "entities/review_email_template";
import { PlaceholderChips } from "./PlaceholderChips";
import { PreviewPanel } from "./PreviewPanel";

const ALL_EDITABLE_FIELDS = Object.values(FIELD_GROUPS).flat();

const SECTION_ICONS: Record<string, React.ReactNode> = {
  "Email Meta": <IconMail size={14} />,
  Content: <IconFileText size={14} />,
  Button: <IconForms size={14} />,
  Footer: <IconMessage size={14} />,
  Styling: <IconPalette size={14} />,
};

const SECTION_COLORS: Record<string, string> = {
  "Email Meta": "blue",
  Content: "teal",
  Button: "grape",
  Footer: "orange",
  Styling: "pink",
};

const TEXTAREA_FIELDS = new Set([
  "greeting_text",
  "product_mention_text",
  "message_body_text",
]);

const COLOR_FIELDS = new Set(["brand_color", "background_color"]);

export const ReviewEmailTemplateEdit: React.FC<
  IResourceComponentsProps
> = () => {
  const apiUrl = useApiUrl();
  const [previewHtml, setPreviewHtml] = useState("");
  const lastFocusedFieldRef = useRef<string | null>(null);
  const inputRefs = useRef<
    Record<string, HTMLInputElement | HTMLTextAreaElement>
  >({});

  const { mutate: fetchPreview, isLoading: isPreviewLoading } =
    useCustomMutation();

  const {
    getInputProps,
    saveButtonProps,
    setFieldValue,
    values,
    refineCore: { queryResult },
  } = useForm<ReviewEmailTemplate, HttpError, UpdateReviewEmailTemplate>({
    initialValues: {
      email_title: "",
      email_subject: "",
      greeting_text: "",
      product_mention_text: "",
      message_body_text: "",
      support_note_text: "",
      section_header_text: "",
      submit_button_text: "",
      footer_trouble_text: "",
      footer_link_text: "",
      brand_color: "",
      background_color: "",
      logo_url: "",
      banner_url: "",
      font_family: "",
      button_border_radius: "",
    },
    refineCoreProps: {
      redirect: false,
      // Disable auto-save - only save when user clicks the save button
      autoSave: {
        enabled: false,
      },
      warnWhenUnsavedChanges: true,
    },
  });

  const template = queryResult?.data?.data;

  // Pre-fill form with existing data
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (!template || defaultsApplied) return;
    for (const field of ALL_EDITABLE_FIELDS) {
      const rec = template as Record<string, unknown>;
      const customValue = rec[field];
      if (customValue != null) {
        setFieldValue(field, customValue as string);
      }
    }
    setDefaultsApplied(true);
  }, [template, defaultsApplied, setFieldValue]);

  // Build preview payload from current form values
  const buildPreviewPayload = useCallback(() => {
    const payload: Record<string, unknown> = {};
    for (const field of ALL_EDITABLE_FIELDS) {
      const val = (values as Record<string, unknown>)[field];
      payload[field] = val === "" || val == null ? null : val;
    }
    return payload;
  }, [values]);

  // Manual preview fetch - only called when user explicitly requests it
  const handleFetchPreview = useCallback(() => {
    if (!template) return;

    const payload = buildPreviewPayload();
    fetchPreview(
      {
        url: `${apiUrl}/review_email_templates/${template.id}/preview`,
        method: "post",
        values: payload,
      },
      {
        onSuccess: (data: any) => {
          setPreviewHtml(data?.data?.html || "");
        },
      },
    );
  }, [template, fetchPreview, buildPreviewPayload, apiUrl]);

  const handleInsertPlaceholder = useCallback(
    (placeholder: string) => {
      const fieldName = lastFocusedFieldRef.current;
      if (!fieldName) return;

      const el = inputRefs.current[fieldName];
      if (!el) return;

      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const currentValue =
        ((values as Record<string, unknown>)[fieldName] as string) || "";
      const newValue =
        currentValue.substring(0, start) +
        placeholder +
        currentValue.substring(end);

      setFieldValue(fieldName, newValue);

      requestAnimationFrame(() => {
        el.focus();
        const cursorPos = start + placeholder.length;
        el.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [setFieldValue, values],
  );

  const handleResetField = useCallback(
    (fieldName: string) => {
      setFieldValue(fieldName, "" as any);
    },
    [setFieldValue],
  );

  const renderField = (fieldName: string) => {
    const label = FIELD_LABELS[fieldName] || fieldName;
    const currentValue = (values as Record<string, unknown>)[fieldName];
    const isCustomized = currentValue != null && currentValue !== "";
    const supportsPlaceholders = PLACEHOLDER_FIELDS.has(fieldName);

    const resetButton = isCustomized ? (
      <Tooltip label="Reset to default">
        <ActionIcon
          size="xs"
          variant="subtle"
          color="gray"
          onClick={() => handleResetField(fieldName)}
        >
          <IconX size={12} />
        </ActionIcon>
      </Tooltip>
    ) : null;

    const handleFocus = () => {
      if (supportsPlaceholders) {
        lastFocusedFieldRef.current = fieldName;
      }
    };

    const setRef = (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      if (el) inputRefs.current[fieldName] = el;
    };

    const fieldWrapper = (align: string, input: React.ReactNode) => (
      <Box
        key={fieldName}
        sx={{ display: "flex", alignItems: align, gap: 6, width: "100%" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>{input}</Box>
        {resetButton}
      </Box>
    );

    if (COLOR_FIELDS.has(fieldName)) {
      return fieldWrapper(
        "flex-end",
        <ColorInput
          size="xs"
          label={label}
          placeholder="Uses default"
          {...getInputProps(fieldName)}
          value={(getInputProps(fieldName).value as string) ?? ""}
        />,
      );
    }

    if (fieldName === "font_family") {
      return fieldWrapper(
        "flex-end",
        <Select
          size="xs"
          label={label}
          placeholder="Uses default"
          data={FONT_OPTIONS}
          clearable
          {...getInputProps(fieldName)}
        />,
      );
    }

    if (TEXTAREA_FIELDS.has(fieldName)) {
      return fieldWrapper(
        "flex-start",
        <Textarea
          size="xs"
          label={label}
          placeholder="Uses default"
          autosize
          minRows={2}
          maxRows={4}
          ref={setRef as any}
          onFocus={handleFocus}
          {...getInputProps(fieldName)}
          value={(getInputProps(fieldName).value as string) ?? ""}
        />,
      );
    }

    return fieldWrapper(
      "flex-end",
      <TextInput
        size="xs"
        label={label}
        placeholder="Uses default"
        ref={setRef as any}
        onFocus={handleFocus}
        {...getInputProps(fieldName)}
        value={(getInputProps(fieldName).value as string) ?? ""}
      />,
    );
  };

  return (
    <Edit footerButtons={() => <></>}>
      <Grid gutter={0}>
        {/* Settings sidebar */}
        <Grid.Col span={4}>
          <Box
            sx={(theme) => ({
              borderRight: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[4]
                  : theme.colors.gray[2]
              }`,
              height: "calc(100vh - 140px)",
              display: "flex",
              flexDirection: "column",
            })}
          >
            {/* Sidebar header: save button + template info */}
            <Box
              sx={(theme) => ({
                padding: "12px 16px",
                borderBottom: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
                flexShrink: 0,
              })}
            >
              <Button
                fullWidth
                leftIcon={<IconDeviceFloppy size={16} />}
                {...saveButtonProps}
                mb={8}
                sx={(theme) => ({
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.pink[7]
                      : theme.colors.pink[6],
                  "&:hover": {
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.pink[6]
                        : theme.colors.pink[7],
                  },
                })}
              >
                Save Changes
              </Button>
              <Button
                fullWidth
                variant="light"
                leftIcon={<IconEye size={16} />}
                onClick={handleFetchPreview}
                loading={isPreviewLoading}
                disabled={!template}
                mb={template ? 10 : 0}
                sx={(theme) => ({
                  borderColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.blue[7]
                      : theme.colors.blue[6],
                  color:
                    theme.colorScheme === "dark"
                      ? theme.colors.blue[4]
                      : theme.colors.blue[7],
                })}
              >
                Preview Email
              </Button>
              {template && (
                <Group spacing={8} noWrap>
                  <Badge size="sm" variant="light" color="pink" radius="sm">
                    {BRAND_LABELS[template.brand] || template.brand}
                  </Badge>
                  <Badge size="sm" variant="light" color="indigo" radius="sm">
                    {EMAIL_TYPE_LABELS[template.email_type] ||
                      template.email_type}
                  </Badge>
                  <Text
                    size="xs"
                    color="dimmed"
                    lineClamp={1}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {template.support_email}
                  </Text>
                </Group>
              )}
            </Box>

            {/* Placeholder chips */}
            <Box
              sx={(theme) => ({
                padding: "6px 16px 8px",
                borderBottom: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
                flexShrink: 0,
              })}
            >
              <Text
                size={9}
                weight={600}
                color="dimmed"
                mb={4}
                sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                Placeholders
              </Text>
              <PlaceholderChips
                placeholders={PLACEHOLDERS}
                onInsert={handleInsertPlaceholder}
              />
            </Box>

            {/* Scrollable form sections */}
            <ScrollArea sx={{ flex: 1 }} offsetScrollbars scrollbarSize={6}>
              <Box sx={{ padding: "8px 16px 16px" }}>
                <Accordion
                  variant="filled"
                  multiple
                  defaultValue={["Email Meta", "Content"]}
                  styles={(theme) => ({
                    control: {
                      padding: "8px 10px",
                      borderRadius: theme.radius.sm,
                      "&:hover": {
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.dark[5]
                            : theme.colors.gray[0],
                      },
                    },
                    item: {
                      backgroundColor: "transparent",
                      border: "none",
                      "&[data-active]": {
                        backgroundColor: "transparent",
                      },
                    },
                    content: {
                      padding: "4px 4px 8px",
                    },
                    chevron: {
                      width: 16,
                      height: 16,
                    },
                  })}
                >
                  {Object.entries(FIELD_GROUPS).map(([sectionName, fields]) => (
                    <Accordion.Item key={sectionName} value={sectionName}>
                      <Accordion.Control>
                        <Group spacing={8}>
                          <ThemeIcon
                            size={22}
                            radius="sm"
                            variant="light"
                            color={SECTION_COLORS[sectionName] || "gray"}
                          >
                            {SECTION_ICONS[sectionName]}
                          </ThemeIcon>
                          <Text size="xs" weight={600}>
                            {sectionName}
                          </Text>
                          <Badge
                            size="xs"
                            variant="filled"
                            color="gray"
                            radius="xl"
                            sx={{ minWidth: 20, height: 16, padding: "0 5px" }}
                          >
                            {fields.length}
                          </Badge>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {fields.map((field) => renderField(field))}
                        </Box>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Box>
            </ScrollArea>
          </Box>
        </Grid.Col>

        {/* Preview canvas */}
        <Grid.Col span={8}>
          <PreviewPanel html={previewHtml} isLoading={isPreviewLoading} />
        </Grid.Col>
      </Grid>
    </Edit>
  );
};
